<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-4">
          <h1 class="text-h4 mb-0">{{ $t('versions.title') }}</h1>
        </div>
      </v-col>
    </v-row>

    <!-- Filters -->
    <v-row class="filter-cards-row">
      <v-col cols="12" md="8">
        <v-card class="filter-card">
          <v-card-title>{{ $t('versions.filters') }}</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="filters.item_type"
                  :items="modelTypes"
                  :label="$t('versions.modelType')"
                  clearable
                  density="compact"
                  @update:model-value="loadVersions"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-text-field
                  v-model="filters.item_id"
                  :label="$t('versions.itemId')"
                  type="number"
                  clearable
                  density="compact"
                  @update:model-value="loadVersions"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="filters.event"
                  :items="eventTypes"
                  :label="$t('versions.event')"
                  clearable
                  density="compact"
                  @update:model-value="loadVersions"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="filters.whodunnit"
                  :items="users"
                  :label="$t('versions.user')"
                  clearable
                  density="compact"
                  item-title="email"
                  item-value="id"
                  @update:model-value="loadVersions"
                ></v-select>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card class="filter-card">
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="filters.date_from"
                  :label="$t('versions.dateFrom')"
                  type="date"
                  density="compact"
                  hide-details
                  @update:model-value="loadVersions"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="filters.date_to"
                  :label="$t('versions.dateTo')"
                  type="date"
                  density="compact"
                  hide-details
                  @update:model-value="loadVersions"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" class="py-1">
                <v-btn-toggle v-model="datePeriod" mandatory class="period-buttons" @update:model-value="handleDatePeriodChange">
                  <v-btn value="day" size="small">{{ $t('transactions.day') }}</v-btn>
                  <v-btn value="week" size="small">{{ $t('transactions.week') }}</v-btn>
                  <v-btn value="month" size="small">{{ $t('transactions.month') }}</v-btn>
                </v-btn-toggle>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Versions List -->
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>
            {{ $t('versions.list') }}
            <v-spacer></v-spacer>
            <span v-if="pagination.total_count" class="text-caption">
              {{ $t('versions.total') }}: {{ pagination.total_count }}
            </span>
          </v-card-title>
          <v-card-text>
            <v-table density="compact">
              <thead>
                <tr>
                  <th>{{ $t('versions.date') }}</th>
                  <th>{{ $t('versions.model') }}</th>
                  <th>{{ $t('versions.itemId') }}</th>
                  <th>{{ $t('versions.event') }}</th>
                  <th>{{ $t('versions.user') }}</th>
                  <th>{{ $t('versions.changes') }}</th>
                  <th>{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="version in versions" :key="version.id">
                  <td>{{ formatDate(version.created_at) }}</td>
                  <td><strong>{{ version.item_type }}</strong></td>
                  <td>{{ version.item_id }}</td>
                  <td>
                    <v-chip
                      :color="getEventColor(version.event)"
                      size="small"
                    >
                      {{ version.event }}
                    </v-chip>
                  </td>
                  <td>{{ version.user_email || '-' }}</td>
                  <td>
                    <span v-if="version.object_changes && Object.keys(version.object_changes).length > 0">
                      {{ Object.keys(version.object_changes).length }} {{ $t('versions.fieldsChanged') }}
                    </span>
                    <span v-else-if="version.event === 'create'">
                      {{ $t('versions.created') }}
                    </span>
                    <span v-else-if="version.event === 'destroy'">
                      {{ $t('versions.destroyed') }}
                    </span>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <v-btn
                      variant="plain"
                      size="small"
                      icon="mdi-eye"
                      @click="viewVersion(version)"
                      class="mr-1"
                      :title="$t('versions.viewDetails')"
                    ></v-btn>
                    <v-btn
                      v-if="getObjectRoute(version)"
                      variant="plain"
                      size="small"
                      icon="mdi-open-in-new"
                      @click="openObject(version)"
                      :disabled="version.event === 'destroy'"
                      :title="version.event === 'destroy' ? $t('versions.objectDeleted') : $t('versions.openObject')"
                    ></v-btn>
                  </td>
                </tr>
                <tr v-if="versions.length === 0 && !loading">
                  <td colspan="7" class="text-center text-grey">
                    {{ $t('versions.noVersionsFound') }}
                  </td>
                </tr>
                <tr v-if="loading">
                  <td colspan="7" class="text-center">
                    {{ $t('common.loading') }}
                  </td>
                </tr>
              </tbody>
            </v-table>

            <!-- Pagination -->
            <div v-if="pagination.total_pages > 1" class="mt-4 d-flex justify-center">
              <v-pagination
                v-model="pagination.page"
                :length="pagination.total_pages"
                :total-visible="7"
                @update:model-value="loadVersions"
              ></v-pagination>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Version Detail Dialog -->
    <v-dialog v-model="showDetailDialog" max-width="900">
      <v-card v-if="selectedVersion">
        <v-card-title>
          {{ $t('versions.versionDetails') }}
          <v-spacer></v-spacer>
          <v-chip
            :color="getEventColor(selectedVersion.event)"
            size="small"
            class="mr-2"
          >
            {{ selectedVersion.event }}
          </v-chip>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="4">
              <v-card variant="outlined">
                <v-card-title class="text-subtitle-1">{{ $t('versions.basicInfo') }}</v-card-title>
                <v-card-text>
                  <p><strong>{{ $t('versions.model') }}:</strong> {{ selectedVersion.item_type }}</p>
                  <p><strong>{{ $t('versions.itemId') }}:</strong> {{ selectedVersion.item_id }}</p>
                  <p><strong>{{ $t('versions.event') }}:</strong> {{ selectedVersion.event }}</p>
                  <p><strong>{{ $t('versions.user') }}:</strong> {{ selectedVersion.user_email || '-' }}</p>
                  <p><strong>{{ $t('versions.date') }}:</strong> {{ formatDate(selectedVersion.created_at) }}</p>
                  <p v-if="selectedVersion.item">
                    <strong>{{ $t('versions.item') }}:</strong> {{ selectedVersion.item.to_s }}
                  </p>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="8">
              <v-card variant="outlined">
                <v-card-title class="text-subtitle-1">
                  <span v-if="selectedVersion.event === 'update'">{{ $t('versions.changes') }}</span>
                  <span v-else-if="selectedVersion.event === 'create'">{{ $t('versions.createdData') }}</span>
                  <span v-else-if="selectedVersion.event === 'destroy'">{{ $t('versions.destroyed') }} {{ $t('versions.item') }}</span>
                  <span v-else>{{ $t('versions.changes') }}</span>
                </v-card-title>
                <v-card-text>
                  <v-table density="compact" v-if="getChangesTableData().length > 0">
                    <thead>
                      <tr>
                        <th>{{ $t('versions.field') }}</th>
                        <th v-if="selectedVersion.event === 'update'">{{ $t('versions.before') }}</th>
                        <th v-if="selectedVersion.event === 'update'">{{ $t('versions.after') }}</th>
                        <th v-if="selectedVersion.event === 'create' || selectedVersion.event === 'destroy'">{{ $t('versions.value') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, index) in getChangesTableData()" :key="index">
                        <td><strong>{{ row.field }}</strong></td>
                        <td v-if="selectedVersion.event === 'update'" v-html="row.before"></td>
                        <td v-if="selectedVersion.event === 'update'" v-html="row.after"></td>
                        <td v-if="selectedVersion.event === 'create' || selectedVersion.event === 'destroy'" v-html="row.value"></td>
                      </tr>
                    </tbody>
                  </v-table>
                  <div v-else class="text-center text-grey py-4">
                    {{ $t('versions.noChanges') }}
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showDetailDialog = false">{{ $t('common.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { apiGet } from '../utils/api'
import { getDateRangeForPeriod } from '../utils/date'

const { t } = useI18n()
const router = useRouter()

interface Version {
  id: number
  item_type: string
  item_id: number
  event: string
  whodunnit: string | null
  user_email: string | null
  object: Record<string, any> | null
  object_changes: Record<string, [any, any]> | null
  created_at: string
  item: { id: number; to_s: string } | null
}

interface User {
  id: string
  email: string
}

const versions = ref<Version[]>([])
const modelTypes = ref<string[]>([])
const users = ref<User[]>([])
const loading = ref(false)
const showDetailDialog = ref(false)
const selectedVersion = ref<Version | null>(null)

const filters = ref({
  item_type: '',
  item_id: '',
  event: '',
  whodunnit: '',
  date_from: '',
  date_to: ''
})

const datePeriod = ref<string>('day')
const userWeekStart = ref<string>('sunday')

const pagination = ref({
  page: 1,
  per_page: 50,
  total_count: 0,
  total_pages: 1
})

const eventTypes = [
  { title: 'Create', value: 'create' },
  { title: 'Update', value: 'update' },
  { title: 'Destroy', value: 'destroy' }
]

const loadModelTypes = async () => {
  try {
    const response = await apiGet('/api/versions/model_types')
    if (response.ok) {
      modelTypes.value = await response.json()
    }
  } catch (error) {
    console.error('Load model types error:', error)
  }
}

const loadUsers = async () => {
  try {
    const response = await apiGet('/api/versions/users')
    if (response.ok) {
      users.value = await response.json()
    }
  } catch (error) {
    console.error('Load users error:', error)
  }
}

const loadVersions = async () => {
  loading.value = true
  try {
    const params = new URLSearchParams()
    if (filters.value.item_type) params.append('item_type', filters.value.item_type)
    if (filters.value.item_id) params.append('item_id', filters.value.item_id)
    if (filters.value.event) params.append('event', filters.value.event)
    if (filters.value.whodunnit) params.append('whodunnit', filters.value.whodunnit)
    if (filters.value.date_from) params.append('date_from', filters.value.date_from)
    if (filters.value.date_to) params.append('date_to', filters.value.date_to)
    params.append('page', pagination.value.page.toString())
    params.append('per_page', pagination.value.per_page.toString())

    const response = await apiGet(`/api/versions?${params.toString()}`)
    if (response.ok) {
      const data = await response.json()
      versions.value = data.versions
      pagination.value = data.pagination
    }
  } catch (error) {
    console.error('Load versions error:', error)
  } finally {
    loading.value = false
  }
}

const resetFilters = () => {
  filters.value = {
    item_type: '',
    item_id: '',
    event: '',
    whodunnit: '',
    date_from: '',
    date_to: ''
  }
  datePeriod.value = 'day'
  pagination.value.page = 1
  setDatePeriod('day')
  loadVersions()
}

// Set date period
const setDatePeriod = (period: string) => {
  const dateRange = getDateRangeForPeriod(period, userWeekStart.value)
  filters.value.date_from = dateRange.from
  filters.value.date_to = dateRange.to
}

// Handle date period change
const handleDatePeriodChange = (period: string) => {
  setDatePeriod(period)
  loadVersions()
}

// Watch date period changes
watch(datePeriod, (newPeriod) => {
  setDatePeriod(newPeriod)
})

// Listen for week start preference changes from Settings page
window.addEventListener('week-start-changed', ((e: CustomEvent) => {
  if (e.detail?.weekStart) {
    userWeekStart.value = e.detail.weekStart
    if (datePeriod.value === 'week') {
      setDatePeriod('week')
      loadVersions()
    }
  }
}) as EventListener)

// Load current user preferences
const loadCurrentUser = async () => {
  try {
    const response = await apiGet('/api/users/current')
    if (response.ok) {
      const userData = await response.json()
      userWeekStart.value = userData.week_start || 'sunday'
    }
  } catch (error) {
    console.error('Load current user error:', error)
  }
}

const viewVersion = async (version: Version) => {
  // Fetch full version details to ensure we have all data
  try {
    const response = await apiGet(`/api/versions/${version.id}`)
    if (response.ok) {
      selectedVersion.value = await response.json()
    } else {
      // Fallback to version from list if API call fails
      selectedVersion.value = version
    }
  } catch (error) {
    console.error('Error loading version details:', error)
    // Fallback to version from list
    selectedVersion.value = version
  }
  showDetailDialog.value = true
}

// Get the route for a version's object based on item_type
const getObjectRoute = (version: Version): string | null => {
  if (!version.item_id) return null
  
  const routeMap: Record<string, (id: number) => string | null> = {
    'Wallet': (id) => `/wallets/${id}`,
    'Transaction': () => `/transactions`, // No detail page, just list
    'Investor': () => `/investors`, // No detail page, just list
    'Settlement': () => `/settlements`, // No detail page, just list
    'Expense': () => `/expenses`, // No detail page, just list
    'User': () => `/users`, // No detail page, just list
    'Role': () => `/roles`, // No detail page, just list
    'Permission': () => `/permissions`, // No detail page, just list
    'Coin': () => `/coins`, // No detail page, just list
    'Exchange': () => `/exchanges`, // No detail page, just list
    'ExpensesType': () => `/expense-types`, // No detail page, just list
    'VersioningConfig': () => `/versioning-configs`, // No detail page, just list
  }
  
  const routeFn = routeMap[version.item_type]
  return routeFn ? routeFn(version.item_id) : null
}

// Navigate to the object's page
const openObject = (version: Version) => {
  const route = getObjectRoute(version)
  if (route) {
    router.push(route)
  }
}

const getEventColor = (event: string) => {
  switch (event) {
    case 'create':
      return 'success'
    case 'update':
      return 'info'
    case 'destroy':
      return 'error'
    default:
      return 'default'
  }
}

const formatDate = (dateString: string) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleString()
}

const formatValue = (value: any) => {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

const formatFieldName = (field: string): string => {
  // Convert snake_case to Title Case
  return field
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const formatExplainedValue = (field: string, value: any, itemType: string): string => {
  if (value === null || value === undefined) return '<span class="text-grey">-</span>'
  
  // Handle string values that might contain enhanced format (e.g., "123 (Name)")
  if (typeof value === 'string') {
    // Check if it's an enhanced foreign key format: "ID (Description)"
    const enhancedMatch = value.match(/^(\d+)\s*\((.+)\)$/)
    if (enhancedMatch) {
      return `<strong>ID: ${enhancedMatch[1]}</strong><br><span class="text-grey">${enhancedMatch[2]}</span>`
    }
    
    // Handle dates in string format
    if (field.includes('_at') || field.includes('date') || field === 'created_at' || field === 'updated_at') {
      try {
        const date = new Date(value)
        if (!isNaN(date.getTime())) {
          return date.toLocaleString()
        }
      } catch (e) {
        // Not a valid date, continue
      }
    }
  }
  
  // Handle dates
  if (field.includes('_at') || field.includes('date') || field === 'created_at' || field === 'updated_at') {
    if (typeof value === 'string') {
      try {
        const date = new Date(value)
        if (!isNaN(date.getTime())) {
          return date.toLocaleString()
        }
      } catch (e) {
        // Not a valid date, continue
      }
    }
  }
  
  // Handle boolean values
  if (typeof value === 'boolean') {
    return value ? '<span class="text-success">Yes</span>' : '<span class="text-grey">No</span>'
  }
  
  // Handle numeric values with formatting
  if (typeof value === 'number') {
    // Format large numbers with commas
    if (field.includes('amount') || field.includes('rate') || field.includes('balance') || field.includes('fee') || field.includes('profit')) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 10
      }).format(value)
    }
    return value.toString()
  }
  
  // Handle IDs that might reference other models
  if (field.endsWith('_id') && typeof value === 'number') {
    // Return the ID with a note that it's a reference
    return `<span class="text-primary">ID: ${value}</span>`
  }
  
  // Handle JSON/objects
  if (typeof value === 'object') {
    return `<pre class="text-caption">${JSON.stringify(value, null, 2)}</pre>`
  }
  
  // Default: return as string, escape HTML
  return String(value).replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

// Get table data for changes display
const getChangesTableData = () => {
  if (!selectedVersion.value) return []
  
  const version = selectedVersion.value
  const tableData: Array<{ field: string; before?: string; after?: string; value?: string }> = []
  
  if (version.event === 'update' && version.object_changes) {
    // For update events, show before/after
    Object.entries(version.object_changes).forEach(([field, change]) => {
      if (Array.isArray(change) && change.length === 2) {
        tableData.push({
          field: formatFieldName(field),
          before: formatExplainedValue(field, change[0], version.item_type),
          after: formatExplainedValue(field, change[1], version.item_type)
        })
      }
    })
  } else if (version.event === 'create' && version.object) {
    // For create events, show all fields with their values
    Object.entries(version.object).forEach(([field, value]) => {
      // Skip internal Rails fields
      if (field === 'id' || field === 'created_at' || field === 'updated_at') {
        return
      }
      tableData.push({
        field: formatFieldName(field),
        value: formatExplainedValue(field, value, version.item_type)
      })
    })
  } else if (version.event === 'destroy' && version.object) {
    // For destroy events, show all fields that were deleted
    Object.entries(version.object).forEach(([field, value]) => {
      tableData.push({
        field: formatFieldName(field),
        value: formatExplainedValue(field, value, version.item_type)
      })
    })
  }
  
  return tableData.sort((a, b) => a.field.localeCompare(b.field))
}

onMounted(async () => {
  await loadCurrentUser()
  loadModelTypes()
  loadUsers()
  // Set default period to today
  setDatePeriod('day')
  loadVersions()
})
</script>

<style scoped>
.filter-cards-row {
  display: flex;
  align-items: stretch;
}

.filter-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.filter-card :deep(.v-card-text) {
  flex: 1;
}

.period-buttons :deep(.v-btn) {
  margin: 0;
  padding: 4px 12px;
  min-width: auto;
  height: 24px;
  max-height: 24px;
}

.period-buttons :deep(.v-btn-toggle__content) {
  gap: 0;
}
</style>
